import React, { useState, useEffect } from "react";
import AppShellLayout from "../layout/AppShellLayout";
import ModuleShellHeader from "../common/ModuleShellHeader";
import EndoManager from "./endo/EndoManager";
import SurgeryManager from "./surgery/SurgeryManager"; // Extraction + Implants
import ProsthoManager from "./prostho/ProsthoManager"; // PFD
import PerioManager from "./perio/PerioManager";
import OrthoDashboard from "../manager/ortho/OrthoDashboard"; // Temporary import until moved
import FollowupManager from "./followups/FollowupManager";
import ErrorBoundary from "../manager/shared/ErrorBoundary";
import { getRole } from "../../services/auth";

import "./Treatments.css";

export type TreatmentsTab = "followups" | "rct" | "extraction" | "prostho" | "surgery" | "ortho" | "perio";

export default function TreatmentsModule() {
    const role = getRole();
    const canAccessOrtho = role === "admin" || role === "manager";
    const [activeTab, setActiveTab] = useState<TreatmentsTab>(() => {
        const saved = localStorage.getItem("treatments:activeTab") as TreatmentsTab | null;
        if (saved === "ortho" && !canAccessOrtho) return "followups";
        return saved || "followups";
    });

    useEffect(() => {
        if (activeTab === "ortho" && !canAccessOrtho) {
            setActiveTab("followups");
            localStorage.setItem("treatments:activeTab", "followups");
        }
    }, [activeTab, canAccessOrtho]);

    const switchTab = (tab: TreatmentsTab) => {
        if (tab === "ortho" && !canAccessOrtho) {
            setActiveTab("followups");
            localStorage.setItem("treatments:activeTab", "followups");
            return;
        }
        setActiveTab(tab);
        localStorage.setItem("treatments:activeTab", tab);
    };

    const renderContent = () => {
        switch (activeTab) {
            case "followups":
                return <FollowupManager />;
            case "rct":
                return <EndoManager />;
            case "extraction":
                return <SurgeryManager mode="extraction" />;
            case "prostho":
                return <ProsthoManager />;
            case "surgery":
                return <SurgeryManager mode="implant" />;
            case "ortho":
                return <OrthoDashboard />;
            case "perio":
                return <PerioManager />;
            default:
                return <div>Module not found</div>;
        }
    };

    return (
        <AppShellLayout
            moduleId="treatments"
            header={
                <ModuleShellHeader
                    brandName="Clinical Treatments"
                    brandSub="Monitor queue and manage multi-step specialized procedures"
                    tabs={[
                        { key: "followups", label: "Follow-ups" },
                        { key: "rct", label: "Endodontics" },
                        { key: "extraction", label: "Exodontia" },
                        { key: "prostho", label: "Prosthodontics" },
                        { key: "surgery", label: "Implantology" },
                        { key: "perio", label: "Periodontics" },
                        ...(canAccessOrtho ? [{ key: "ortho", label: "Orthodontics" }] : [])
                    ]}
                    activeTab={activeTab}
                    onTabChange={(key: string) => switchTab(key as TreatmentsTab)}
                />
            }
            left={undefined}
            leftCollapsible={false}
            main={
                <ErrorBoundary>
                    <div className="treatments-workspace">
                        {renderContent()}
                    </div>
                </ErrorBoundary>
            }
        />
    );
}
